import { AlertTriangle, Check, X, Play, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/currency';

const PLAN = {
  peppol_requested: { label: 'Peppol wordt gevraagd', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  manual_peppol: { label: 'Handmatig in Yuki (B2B)', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  unknown: { label: 'Onbekend / nazicht (B2B)', tone: 'bg-orange-50 text-orange-800 border-orange-200' },
  not_applicable: { label: 'N.v.t. — alleen boeking', tone: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const OUTCOME = {
  booked_only: { label: 'Geboekt (geen Peppol)', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  booked_peppol_requested_delivery_unconfirmed: { label: 'Geboekt + Peppol aangevraagd — aflevering onbevestigd', tone: 'bg-sky-50 text-sky-800 border-sky-200' },
  booked_peppol_skipped: { label: 'Geboekt, Peppol door Yuki geweigerd', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  failed_peppol_recipient: { label: 'Peppol niet beschikbaar — controleer de optie Exporteren zonder Peppol in het factuurdetail', tone: 'bg-red-50 text-red-800 border-red-200' },
  failed_peppol_sender_permission: { label: 'Fout: Yuki-domein mag niet via Peppol verzenden — geen retry', tone: 'bg-red-50 text-red-800 border-red-200' },
  failed_other: { label: 'Fout', tone: 'bg-red-50 text-red-800 border-red-200' },
  skipped_locked: { label: 'Overgeslagen (vergrendeld/eerder gepoogd)', tone: 'bg-gray-100 text-gray-600 border-gray-200' },
};

function Badge({ def, testid }) {
  return <span data-testid={testid} className={`inline-flex items-center text-xs px-2 py-0.5 rounded border ${def.tone}`}>{def.label}</span>;
}

export function ProcessPendingPreflight({ preview, running, onConfirm, onCancel }) {
  const c = preview.counts || {};
  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4" data-testid="process-pending-preflight">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Vooraf: {preview.total} PENDING export(s) worden verwerkt</h3>
        <span className="text-xs text-gray-600" data-testid="preflight-global-flag">
          Peppol Auto Send: <strong>{preview.globalPeppolAutoSend ? 'AAN' : 'UIT (handmatig in Yuki)'}</strong>
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3" data-testid="preflight-counts">
        {c.peppol_requested || 0} Peppol gevraagd · {c.manual_peppol || 0} handmatig (B2B) · {c.unknown || 0} onbekend · {c.not_applicable || 0} n.v.t. — {preview.note}
      </p>
      <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto" data-testid="preflight-list">
        {preview.items.map((it) => (
          <li key={it.exportId} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`preflight-item-${it.plan}`}>
            <span className="font-mono font-semibold text-gray-900">{it.invoiceNumber || '—'}</span>
            <span className="text-gray-600 truncate max-w-[240px]">{it.customerName || '—'}</span>
            {it.totalInclVat != null && <span className="text-gray-500 text-xs">{formatCurrency(it.totalInclVat)}</span>}
            <Badge def={PLAN[it.plan] || PLAN.not_applicable} />
            {!it.neverAttempted && <span className="text-xs text-red-700">eerder gepoogd — wordt geblokkeerd</span>}
            {it.warnings?.length > 0 && <span className="text-xs text-orange-700">{it.warnings.join(', ')}</span>}
            <span className="text-xs text-gray-500 basis-full">{it.label}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <button onClick={onConfirm} disabled={running || !preview.total || !preview.yukiEnabled} data-testid="process-pending-confirm-btn"
          className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Bevestig & verwerk {preview.total}
        </button>
        <button onClick={onCancel} disabled={running} data-testid="process-pending-cancel-btn" className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">Annuleren</button>
      </div>
    </div>
  );
}

export function ProcessPendingResults({ result, onClose }) {
  const s = result.peppolSummary || {};
  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4" data-testid="process-pending-results">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Resultaat per factuur</h3>
        <button onClick={onClose} className="text-xs text-gray-500 underline" data-testid="process-pending-results-close">Sluiten</button>
      </div>
      <p className="text-xs text-gray-600 mb-3" data-testid="results-summary">
        {s.bookedOnly || 0} geboekt · {s.peppolRequestedDeliveryUnconfirmed || 0} Peppol aangevraagd (aflevering onbevestigd) · {s.bookedPeppolSkipped || 0} Peppol geweigerd · {(s.failedPeppolRecipient || 0) + (s.failedPeppolSenderPermission || 0) + (s.failedOther || 0)} fout · {s.skippedLocked || 0} overgeslagen. Controleer Peppol-aflevering in de Yuki-UI.
      </p>
      <ul className="divide-y divide-gray-100" data-testid="results-list">
        {(result.details || []).map((d) => (
          <li key={d.exportId} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`result-item-${d.peppolOutcome}`}>
            {d.success ? <Check className="w-3 h-3 text-emerald-700" /> : d.peppolOutcome === 'skipped_locked' ? <AlertTriangle className="w-3 h-3 text-gray-500" /> : <X className="w-3 h-3 text-red-700" />}
            <span className="font-mono font-semibold text-gray-900">{d.invoiceNumber || d.exportId}</span>
            <Badge def={OUTCOME[d.peppolOutcome] || OUTCOME.failed_other} />
            {d.externalDocumentId && <span className="text-xs text-gray-500">Yuki doc {d.externalDocumentId}</span>}
            {!d.success && d.message && <span className="text-xs text-gray-600 basis-full">{d.message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const PLAN_TONE = {
  peppol_requested: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  manual_peppol: 'bg-amber-50 border-amber-200 text-amber-800',
  unknown: 'bg-gray-50 border-gray-200 text-gray-700',
  not_applicable: 'bg-gray-50 border-gray-200 text-gray-700',
};

export function ExportNowPreflight({ preview, running, onConfirm, onCancel }) {
  const money = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
  const blocked = !preview.yukiEnabled || preview.exportStatus === 'EXPORTED' || !preview.neverAttempted;
  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4" data-testid="export-now-preflight">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">Export Now — verzendplan voor factuur {preview.invoiceNumber || '—'}</h3>
          <p className="text-xs text-gray-500 mt-1">Nog niets verzonden. Bevestig exact dit plan; wijzigt de factuur, klant, het bedrag, de exportstatus of de Peppol-instelling ondertussen, dan weigert de server (geen Yuki-aanroep).</p>
        </div>
        <span data-testid={`export-now-plan-${preview.plan}`} className={`text-xs px-2 py-1 rounded border ${PLAN_TONE[preview.plan] || PLAN_TONE.unknown}`}>{preview.label}</span>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
        <div><dt className="text-gray-500">Klant</dt><dd className="font-medium text-gray-900" data-testid="export-now-customer">{preview.customerName || '—'} · {preview.countryCode || '—'} · btw {preview.vatMasked || '—'}</dd></div>
        <div><dt className="text-gray-500">Bedragen</dt><dd className="font-mono text-gray-900" data-testid="export-now-amounts">excl {money(preview.subtotalExVat)} · btw {money(preview.vatTotal)} · totaal {money(preview.totalInclVat)}</dd></div>
        <div><dt className="text-gray-500">Status</dt><dd className="text-gray-900">factuur {preview.invoiceStatus} · export {preview.exportStatus || 'geen'} · pogingen {preview.attemptCount}</dd></div>
        <div><dt className="text-gray-500">Peppol Auto Send</dt><dd className="text-gray-900">{preview.globalPeppolAutoSend ? 'AAN' : 'OFF (handmatig in Yuki)'} · Yuki {preview.yukiEnabled ? 'aan' : 'uit'}</dd></div>
      </dl>
      {(preview.warnings || []).length > 0 && <p className="text-xs text-amber-700 mt-2">{preview.warnings.join(' · ')}</p>}
      <p className="text-[11px] text-gray-500 mt-2">Boeking in Yuki ≠ Peppol-aflevering. Bij "Peppol wordt gevraagd" bevestigt Commerce nooit de aflevering; controleer in de Yuki-UI.</p>
      <div className="flex gap-2 mt-3">
        <button onClick={onConfirm} disabled={running || blocked} data-testid="export-now-confirm-btn"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
          {running ? 'Bezig…' : 'Bevestig & exporteer deze factuur'}
        </button>
        <button onClick={onCancel} disabled={running} data-testid="export-now-cancel-btn" className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">Annuleren</button>
      </div>
    </div>
  );
}
